'use client'

import type { PermissionGrant } from '@/features/application/applicationTypes'
import { InitialAuthMeContext } from '@/features/application/components/permissions-provider'
import {
  type AuthMeResponse,
  useGetAuthMeQuery
} from '@/stores/api/application-api'
import { useSession } from '@/utils/auth/auth-client'

import { useContext, useEffect, useMemo } from 'react'

import type {
  Entity,
  ActionsForEntity,
  PermissionPair
} from '@wci/wv-db-schemas'
// Value imports come from the client-safe config subpaths — the package root
// pulls in the drizzle/pg driver, which cannot bundle in the browser.
import { exportGrantId, type DataTableId } from '@wci/wv-db-schemas/data-tables'
import { reportGrantId, type ReportId } from '@wci/wv-db-schemas/reports'

type PermissionKey = `${string}:${string}`

export function usePermissions(): {
  isSystemAdmin: boolean
  permissionGrants: PermissionGrant[]
  permissionKeys: PermissionKey[]
  resolvedPermissions: PermissionPair[]
  isLoading: boolean
  can: <E extends Entity>(entity: E, action: ActionsForEntity<E>) => boolean
  hasPermissionKey: (key: PermissionKey) => boolean
  canExportTable: (tableId: DataTableId) => boolean
  canViewReport: (reportId: ReportId) => boolean
} {
  const { data: session, isPending: sessionLoading } = useSession()
  const userId = session?.user?.id

  // Server-resolved auth payload, available synchronously during SSR and on the
  // first client paint via context (see PermissionsProvider). This is the
  // source of truth for the first render.
  const initialAuthMe = useContext(InitialAuthMeContext)

  // The live query handles runtime freshness (focus refetch, tag invalidation
  // after admin permission mutations) and stays skipped until the client
  // session resolves so logged-out users never hit /auth/me.
  const {
    data: liveData,
    isLoading,
    refetch
  } = useGetAuthMeQuery(undefined, {
    skip: !userId
  })

  // A payload stamped for another user is left over from the identity this tab
  // had before an impersonation started or ended (the cache key carries no
  // user). Rendering it showed an impersonator their own apps; drop it, and
  // refetch if it is the live entry.
  const belongsToSession = (payload: AuthMeResponse | null | undefined) =>
    !payload?.userId || !userId || payload.userId === userId
  const liveIsStale = !!liveData && !belongsToSession(liveData)
  useEffect(() => {
    if (liveIsStale) refetch()
  }, [liveIsStale, refetch])

  // Context first so SSR and the first client render agree (useSession()/RTK
  // are both empty/unresolved at that point); the live query takes over once it
  // resolves. Both yield the same value on the first render, so no can()-gated
  // branch (sidebar footer, app switcher) can diverge → no hydration mismatch.
  const data =
    (liveIsStale ? undefined : liveData) ??
    (belongsToSession(initialAuthMe) ? initialAuthMe : undefined) ??
    undefined

  const grants = useMemo(() => data?.permissionGrants ?? [], [data])
  const catalog = useMemo(() => data?.catalog ?? [], [data])
  const isSystemAdmin = data?.isSystemAdmin ?? false

  const permissionKeys = useMemo(
    (): PermissionKey[] => grants.map((g) => g.permissionId as PermissionKey),
    [grants]
  )

  const resolvedPermissions = useMemo(() => {
    return grants
      .map((grant) => {
        const match = catalog.find((c) => c.id === grant.permissionId)
        if (!match) return null
        return { entity: match.entity, action: match.action } as PermissionPair
      })
      .filter(Boolean) as PermissionPair[]
  }, [grants, catalog])

  const can = useMemo(
    () =>
      <E extends Entity>(entity: E, action: ActionsForEntity<E>) =>
        permissionKeys.includes(`${entity}:${action}`),
    [permissionKeys]
  )

  const hasPermissionKey = useMemo(
    () => (key: PermissionKey) => permissionKeys.includes(key),
    [permissionKeys]
  )

  // Table-capability grants ride permissionGrants like any permission but are
  // not catalog entries, so `can()` never sees them — this is the dedicated
  // check. No isSystemAdmin bypass by design (RULE-P-01): system admins get
  // export where their roles grant it.
  //
  // The grant id comes from `exportGrantId` rather than being built here,
  // because a table that renders a report is gated on the REPORT's grant
  // (`report:<id>:export`) and every other table on its own
  // (`table:<id>:export`). Deciding that at the call site is what previously
  // let the two drift: three roles held report export grants that this hook
  // never looked at, so Export stayed hidden for all of them.
  const canExportTable = useMemo(
    () => (tableId: DataTableId) => {
      const grantId = exportGrantId(tableId)
      return (
        grantId !== undefined &&
        permissionKeys.includes(grantId as PermissionKey)
      )
    },
    [permissionKeys]
  )

  // Report grants (`report:<reportId>:read`) work the same way and for the same
  // reason — not catalog entries, so `can()` never sees them. This is what
  // gates a report's nav item, its page and its API route, which is why no
  // caller anywhere compares an org slug. Same no-isSystemAdmin-bypass rule as
  // table exports (RULE-P-01): a system admin sees a report where a role
  // grants it, not because of who they are.
  const canViewReport = useMemo(
    () => (reportId: ReportId) =>
      permissionKeys.includes(reportGrantId(reportId, 'read') as PermissionKey),
    [permissionKeys]
  )

  // If we already have a resolved payload (from context on the first render, or
  // the live query after), we're not loading — regardless of where the client
  // session is in its resolution. Keeps isLoading identical across SSR/paint so
  // it can't become its own hydration divergence. A dropped stale payload is
  // loading too: RTK's isLoading is false during a refetch, and guards would
  // otherwise read "no grants" and redirect.
  const combinedLoading =
    !data && (sessionLoading || !userId || isLoading || liveIsStale)

  return {
    isSystemAdmin,
    permissionGrants: grants,
    permissionKeys,
    resolvedPermissions,
    isLoading: combinedLoading,
    can,
    hasPermissionKey,
    canExportTable,
    canViewReport
  }
}
